import React, { Component } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import ExpenseService from "../services/expense.service";
import FamilyService from "../services/family.service";
import AuthService from "../services/auth.service";
import UserService from "../services/user.service";
import EventBus from "../common/EventBus";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF1919'];
const COLORS2 = ['#FF19FF', '#19AFFF', '#FFC107'];

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

class AnimatedNumber extends React.Component {
  constructor(props) {
    super(props);
    this.state = { currentValue: 0 };
    this.interval = null;
  }

  componentDidMount() {
    this.startAnimation(this.props.value);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.startAnimation(this.props.value);
    }
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  startAnimation(targetValue) {
    if (this.interval) {
      clearInterval(this.interval);
    }
    const duration = 1000; // Animation duration in ms
    const stepTime = 16; // ~60 fps
    const steps = duration / stepTime;
    const increment = (targetValue - this.state.currentValue) / steps;
    let current = this.state.currentValue;

    this.interval = setInterval(() => {
      current += increment;
      if ((increment > 0 && current >= targetValue) || (increment < 0 && current <= targetValue)) {
        current = targetValue;
        clearInterval(this.interval);
      }
      this.setState({ currentValue: current });
    }, stepTime);
  }

  render() {
    return <strong>{this.props.value > 0 ? this.state.currentValue.toFixed(2) : "0.00"}</strong>;
  }
}

export default class Monthly extends Component {
  constructor(props) {
    super(props);
    const initialDate = new Date();
    this.state = {
      tableData: [],
      isPaid: false,
      currentDate: initialDate,
      types: [],
      owners: [],
      expenses: [],
      familyMembers: [],
      currentMonth: new Date().getMonth() + 1,
      currentYear: new Date().getFullYear(),
      showEditExpenseModal: false,
      editingExpense: null,
      showDeleteModal: false,
      deletingExpense: null,
      message: "",
      searchTerm: "",
      currentOverviewNote: null,
      sortColumn: null,
      sortDirection: "asc",
      expandedTable: false,
    };

    this.handleShowEditExpenseModal = this.handleShowEditExpenseModal.bind(this);
    this.handleCloseEditModal = this.handleCloseEditModal.bind(this);
    this.handleEditExpenseChange = this.handleEditExpenseChange.bind(this);
    this.submitEditExpense = this.submitEditExpense.bind(this);
    this.handleShowDeleteModal = this.handleShowDeleteModal.bind(this);
    this.handleCloseDeleteModal = this.handleCloseDeleteModal.bind(this);
    this.handleDeleteExpense = this.handleDeleteExpense.bind(this);
  }

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  handleSort = (column) => {
    this.setState(prevState => ({
      sortColumn: column,
      sortDirection: prevState.sortColumn === column && prevState.sortDirection === "asc" ? "desc" : "asc"
    }));
  };

  toggleExpandedTable = () => {
    this.setState(prevState => ({ expandedTable: !prevState.expandedTable }));
  };

  componentDidMount() {
    const currentUser = AuthService.getCurrentUser();
    const selectedFamily = JSON.parse(localStorage.getItem("selectedFamily"));
    
    if (selectedFamily) {
      this.retrieveExpenses(this.state.currentYear, this.state.currentMonth, selectedFamily.id);
      this.fetchCurrentMonthOverviewNote(selectedFamily.id);
    } else if (currentUser && currentUser.families && currentUser.families.length > 0) {
      const family = currentUser.families[0];
      localStorage.setItem("selectedFamily", JSON.stringify(family));
      this.retrieveExpenses(this.state.currentDate.getFullYear(), this.state.currentDate.getMonth() + 1, family.id);
      this.fetchCurrentMonthOverviewNote(family.id);
    }

  }

  retrieveExpenses(year, month, familyId) {
      Promise.all([
        FamilyService.getExpensesByMonthYearFamilyId(month, year, familyId),
        FamilyService.getFamilyMembers(familyId)
      ]).then(([expensesResponse, membersResponse]) => {
          const expenses = expensesResponse.data;
          const types = [...new Set(expenses.map(e => e.type))];
          const owners = [...new Set(expenses.map(e => e.owner))];
          this.setState({
            tableData: expenses,
            types: types,
            owners: owners,
            expenses: expenses,
            familyMembers: membersResponse.data,
            currentMonth: month,
            currentYear: year
          }, () => {
            // This will be executed after the state has been updated
            console.log("Expenses and family members updated:", this.state.expenses, this.state.familyMembers);
          });
        })
        .catch((e) => {
          console.error("Error fetching expenses or family members", e);
        });
  }

  async fetchCurrentMonthOverviewNote(familyId) {
    this.fetchOverviewNoteForDate(familyId, this.state.currentDate);
  }

  async fetchOverviewNoteForDate(familyId, date) {
    try {
      const response = await FamilyService.getOverviewNotesByFamilyId(familyId);
      const overviewNotes = response.data;
      
      // Find the note for the specified month/year
      const currentNote = overviewNotes.find(note => {
        const noteDate = new Date(note.date);
        return noteDate.getFullYear() === date.getFullYear() && 
               noteDate.getMonth() === date.getMonth();
      });
      
      this.setState({
        currentOverviewNote: currentNote,
        isPaid: currentNote ? currentNote.paid : false
      });
    } catch (error) {
      console.error("Error fetching overview notes:", error);
    }
  }

  handleShowEditExpenseModal = (expense) => {
    this.setState({ showEditExpenseModal: true, editingExpense: { ...expense }, message: "" });
  }

  handleCloseEditModal = () => {
    this.setState({ showEditExpenseModal: false, editingExpense: null, message: "" });
  }

  handleEditExpenseChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState(prevState => ({
      editingExpense: {
        ...prevState.editingExpense,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  }

  handleParticipantsChange = (memberId) => {
    this.setState(prevState => {
      const currentParticipants = prevState.editingExpense.participants || [];
      const isSelected = currentParticipants.includes(memberId);
      
      const updatedParticipants = isSelected
        ? currentParticipants.filter(id => id !== memberId)
        : [...currentParticipants, memberId];
      
      return {
        editingExpense: {
          ...prevState.editingExpense,
          participants: updatedParticipants
        }
      };
    });
  }

  handleShowDeleteModal = (expense) => {
    this.setState({ showDeleteModal: true, deletingExpense: expense, message: "" });
  }

  handleCloseDeleteModal = () => {
    this.setState({ showDeleteModal: false, deletingExpense: null, message: "" });
  }

  handleDeleteExpense = () => {
    const { deletingExpense } = this.state;
    const familyId = JSON.parse(localStorage.getItem("selectedFamily")).id;

    if (deletingExpense && familyId) {
      FamilyService.removeExpenseFromFamily(familyId, deletingExpense._id)
        .then(() => {
          this.retrieveExpenses(this.state.currentYear, this.state.currentMonth, familyId);
          this.handleCloseDeleteModal();
        })
        .catch((e) => {
          this.setState({
            message:
              (e.response && e.response.data && e.response.data.message) ||
              e.message ||
              "An error occurred while deleting the expense.",
          });
        });
    }
  }

  submitEditExpense = () => {
    const { editingExpense } = this.state;
    ExpenseService.update(editingExpense._id, editingExpense)
      .then(() => {
        this.retrieveExpenses(this.state.currentYear, this.state.currentMonth, JSON.parse(localStorage.getItem("selectedFamily")).id);
        this.handleCloseEditModal();
      })
      .catch((e) => {
        this.setState({
          message:
            (e.response && e.response.data && e.response.data.message) ||
            e.message ||
            "An error occurred while updating the expense.",
        });
      });
  }

  getOwnerUsername = (ownerId) => {
    const { familyMembers } = this.state;
    const member = familyMembers.find(m => m._id === ownerId);
    return member ? member.username : ownerId;
  };

  getParticipantsUsernames = (participantIds) => {
    if (!participantIds || !Array.isArray(participantIds) || participantIds.length === 0) {
      return "None";
    }
    const { familyMembers } = this.state;
    const participantNames = participantIds.map(participantId => {
      const member = familyMembers.find(m => m._id === participantId);
      return member ? member.username : participantId;
    });
    return participantNames.join(", ");
  };

  togglePaidStatus = async () => {
    const selectedFamily = JSON.parse(localStorage.getItem("selectedFamily"));
    if (!selectedFamily) {
      console.error("No family selected");
      return;
    }

    const familyId = selectedFamily.id;
    const newPaidStatus = !this.state.isPaid;
    const { currentOverviewNote } = this.state;

    try {
      if (currentOverviewNote) {
        // Update existing overview note
        await FamilyService.setOverviewNotePaidStatus(currentOverviewNote._id, newPaidStatus);
      } else {
        // Create new overview note with paid status
        const overviewNoteData = {
          year: this.state.currentDate.getFullYear(),
          month: months[this.state.currentDate.getMonth()],
          note: '',
          paid: newPaidStatus,
        };
        await FamilyService.addOverviewNoteToFamily(familyId, overviewNoteData);
      }
      
      // Refresh the overview note data
      this.fetchOverviewNoteForDate(familyId, this.state.currentDate);
    } catch (error) {
      console.error("Error updating paid status:", error);
    }
  }

  handlePreviousMonth = () => {
    this.setState(prevState => {
      const newDate = new Date(prevState.currentDate);
      newDate.setMonth(newDate.getMonth() - 1);
      const familyId = JSON.parse(localStorage.getItem("selectedFamily")).id;
      this.retrieveExpenses(newDate.getFullYear(), newDate.getMonth() + 1, familyId);
      this.fetchOverviewNoteForDate(familyId, newDate);
      return { currentDate: newDate };
    });
  };

  handleNextMonth = () => {
    this.setState(prevState => {
      const newDate = new Date(prevState.currentDate);
      newDate.setMonth(newDate.getMonth() + 1);
      const familyId = JSON.parse(localStorage.getItem("selectedFamily")).id;
      this.retrieveExpenses(newDate.getFullYear(), newDate.getMonth() + 1, familyId);
      this.fetchOverviewNoteForDate(familyId, newDate);
      return { currentDate: newDate };
    });
  };

  getPieDataByType() {
    const { tableData, types } = this.state;
    const sums = types.map(type => ({
      name: type,
      value: tableData.filter(row => row.type === type).reduce((acc, row) => acc + (row.tr ? row.amount * 0.75 : row.amount), 0)
    }));
    return sums;
  }

  getPieDataByOwner() {
    const { tableData, owners } = this.state;
    const sums = owners.map(owner => ({
      name: this.getOwnerUsername(owner),
      value: tableData.filter(row => row.owner === owner).reduce((acc, row) => acc + (row.tr ? row.amount * 0.75 : row.amount), 0)
    }));
    return sums;
  }

  calculateParticipantBasedBalances() {
    const { tableData, familyMembers } = this.state;
    const eligibleMembers = familyMembers.filter(member => !member.username.includes('-common'));
    
    // Initialize balances for all eligible members
    const memberBalances = {};
    eligibleMembers.forEach(member => {
      const memberId = member._id || member.id;
      memberBalances[memberId] = {
        name: member.username,
        paid: 0,        // Amount this person paid (as owner)
        owes: 0,        // Amount this person owes (as participant)
        balance: 0      // Final balance (paid - owes)
      };
    });

    console.log("Eligible members:", eligibleMembers);
    console.log("Initial member balances:", memberBalances);

    // Filter out expenses owned by '-common' users
    const eligibleExpenses = tableData.filter(expense => {
      const owner = familyMembers.find(member => member._id === expense.owner || member.id === expense.owner);
      return owner && !owner.username.includes('-common');
    });

    console.log("Eligible expenses (excluding '-common' owned):", eligibleExpenses);

    // Process each expense
    eligibleExpenses.forEach(expense => {
      const expenseAmount = expense.tr ? expense.amount * 0.75 : expense.amount;
      
      console.log(`Processing expense: ${expense.name}, amount: ${expenseAmount}, owner: ${expense.owner}, participants:`, expense.participants);
      
      // Add to owner's "paid" amount
      if (memberBalances[expense.owner]) {
        memberBalances[expense.owner].paid += expenseAmount;
      } else {
        console.warn(`Owner ${expense.owner} not found in member balances`);
      }
      
      // Determine participants for this expense
      let participants = [];
      if (expense.participants && Array.isArray(expense.participants) && expense.participants.length > 0) {
        // Use specified participants - might be usernames or IDs
        participants = expense.participants;
      } else {
        // If no participants specified, include all eligible family members
        participants = eligibleMembers.map(member => member._id || member.id);
        console.log("No participants specified, using all members:", participants);
      }
      
      // Convert participant usernames to IDs if needed
      const participantIds = participants.map(participant => {
        // First try to find by ID
        if (memberBalances[participant]) {
          return participant;
        }
        // If not found, try to find by username (in case participants are stored as usernames)
        const memberByUsername = eligibleMembers.find(member => member.username === participant);
        if (memberByUsername) {
          return memberByUsername._id || memberByUsername.id;
        }
        console.warn(`Participant ${participant} not found in family members`);
        return null;
      }).filter(id => id !== null);
      
      console.log("Resolved participant IDs:", participantIds);
      
      // Split expense among participants
      if (participantIds.length > 0) {
        const amountPerParticipant = expenseAmount / participantIds.length;
        participantIds.forEach(participantId => {
          if (memberBalances[participantId]) {
            memberBalances[participantId].owes += amountPerParticipant;
          }
        });
      }
    });

    // Calculate final balances
    Object.keys(memberBalances).forEach(memberId => {
      const member = memberBalances[memberId];
      member.balance = member.paid - member.owes;
    });

    console.log("Final member balances:", memberBalances);
    return Object.values(memberBalances);
  }

  render() {
    const { tableData, types, owners, showEditExpenseModal, editingExpense, message, familyMembers, showDeleteModal, deletingExpense, searchTerm, sortColumn, sortDirection, expandedTable } = this.state;
    const pieData = this.getPieDataByType();
    const pieData2 = this.getPieDataByOwner();

    // Filter expenses based on search term
    const filteredExpenses = tableData
      .filter(expense =>
        Object.values(expense).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
      .sort((a, b) => {
        if (!sortColumn) return 0;
        let aVal, bVal;
        if (sortColumn === "date") {
          aVal = new Date(a.date);
          bVal = new Date(b.date);
          return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        } else if (sortColumn === "amount") {
          aVal = a.tr ? a.amount * 0.75 : a.amount;
          bVal = b.tr ? b.amount * 0.75 : b.amount;
          return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        } else if (sortColumn === "name") {
          aVal = (a.name || "").toLowerCase();
          bVal = (b.name || "").toLowerCase();
        } else if (sortColumn === "type") {
          aVal = (a.type || "").toLowerCase();
          bVal = (b.type || "").toLowerCase();
        } else if (sortColumn === "owner") {
          aVal = (this.getOwnerUsername(a.owner) || "").toLowerCase();
          bVal = (this.getOwnerUsername(b.owner) || "").toLowerCase();
        } else if (sortColumn === "location") {
          aVal = (a.location || "").toLowerCase();
          bVal = (b.location || "").toLowerCase();
        } else {
          return 0;
        }
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });

    // Calculate participant-based debt balances
    const memberBalances = this.calculateParticipantBasedBalances();
    
    let debtInfo = { message: "", relationships: [] };
    
    if (memberBalances.length < 2) {
        debtInfo.message = "Not enough family members for debt calculation.";
    } else {
        // Sort by balance descending (positive = owed money, negative = owes money)
        const sortedBalances = memberBalances.sort((a, b) => b.balance - a.balance);
        
        // Separate creditors (positive balance) and debtors (negative balance)
        const creditors = sortedBalances.filter(member => member.balance > 0.01);
        const debtors = sortedBalances.filter(member => member.balance < -0.01);
        
        if (creditors.length === 0 && debtors.length === 0) {
            debtInfo.message = "Everyone is even.";
        } else {
            // Create debt relationships
            const relationships = [];
            
            // Simple approach: each debtor owes each creditor proportionally
            debtors.forEach(debtor => {
                const totalDebtorOwes = Math.abs(debtor.balance);
                creditors.forEach(creditor => {
                    const totalCreditorOwed = creditors.reduce((sum, c) => sum + c.balance, 0);
                    const creditorShare = creditor.balance / totalCreditorOwed;
                    const amountOwed = totalDebtorOwes * creditorShare;
                    if (amountOwed > 0.01) {
                        relationships.push({
                            debtor: debtor.name,
                            creditor: creditor.name,
                            amount: amountOwed
                        });
                    }
                });
            });
            
            debtInfo.relationships = relationships;
            if (relationships.length === 0) {
                debtInfo.message = "All debts are minimal.";
            }
        }
    }


    return (
      <div className="card">
        <div className="card-body">
        <div className="month-navigation-container">
          <div className="month-navigation">
            <button className="btn btn-light" onClick={this.handlePreviousMonth}>&lt;</button>
            <span className="month-display">
              {this.state.currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </span>
            <button className="btn btn-light" onClick={this.handleNextMonth}>&gt;</button>
          </div>
          <button
            className={`btn ${this.state.isPaid ? 'btn-success' : 'btn-danger'}`}
            onClick={this.togglePaidStatus}
          >
            {this.state.isPaid ? 'Paid' : 'Not Paid'}
          </button>
        </div>
        <h4 className="text-center" style={{ marginBottom: '2rem' }}>
          {debtInfo.message && <div>{debtInfo.message}</div>}
          {debtInfo.relationships.length > 0 && (
            <div>
              <div style={{ marginBottom: '1rem', fontWeight: 'bold' }}>Debt Summary:</div>
              {debtInfo.relationships.map((relationship, index) => (
                <div key={index} style={{ marginBottom: '0.5rem' }}>
                  {relationship.debtor} owes {relationship.creditor} <AnimatedNumber value={relationship.amount} /> euros
                </div>
              ))}
            </div>
          )}
        </h4>
        <div className="summary-tables-container">
          <table className="table summary-table">
            <thead>
              <tr>
                {types.map(type => <th key={type}>{type}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                {types.map(type => (
                  <td key={type} data-label={type}>{(pieData.find(d => d.name === type)?.value || 0).toFixed(2)}</td>
                ))}
              </tr>
            </tbody>
          </table>
          <table className="table summary-table">
            <thead>
              <tr>
                {owners.map(owner => <th key={owner}>{this.getOwnerUsername(owner)}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                {owners.map(owner => (
                  <td key={owner} data-label={this.getOwnerUsername(owner)}>{(pieData2.find(d => d.name === this.getOwnerUsername(owner))?.value || 0).toFixed(2)}</td>
                ))}
              </tr>
            </tbody>
          </table>
          <table className="table summary-table">
            <thead>
              <tr>
                <th>Overall Spending</th>
                <th> Half Spending</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {/* Overall Spending */}
                <td data-label="Overall Spending">
                  {tableData.reduce((acc, row) => acc + (row.tr ? row.amount * 0.75 : row.amount), 0).toFixed(2)}
                </td>
                {/* Half Spending */}
                <td data-label="Half Spending">
                  {(tableData.reduce((acc, row) => acc + (row.tr ? row.amount * 0.75 : row.amount), 0) / 2).toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="pie-charts-container">
          <div className="pie-chart-wrapper">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="pie-chart-wrapper">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData2}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${this.getOwnerUsername(name)} ${(percent * 100).toFixed(0)}%`}>
                  {pieData2.map((entry, index) => (
                    <Cell key={`cell2-${index}`} fill={COLORS2[index % COLORS2.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="form-group" style={{ maxWidth: '400px', margin: '1rem auto' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search..."
              value={searchTerm}
              onChange={this.handleSearchChange}
            />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={this.toggleExpandedTable}>
            {expandedTable ? "⊖ Collapse" : "⊕ Expand columns"}
          </button>
        </div>
        <div className="main-table-container">
          <table className={`table table-sm mt-0 main-table${expandedTable ? ' expanded' : ''}`}>
            <thead>
              <tr>
                {[
                  { col: "name", always: true },
                  { col: "date", always: false },
                  { col: "type", always: false },
                  { col: "owner", always: false },
                  { col: "tr", always: false },
                  { col: "amount", always: false },
                  { col: "amount corrected", always: true },
                  { col: "location", always: false },
                  { col: "notes", always: false },
                  { col: "participants", always: false },
                  { col: "publisher", always: false },
                  { col: "actions", always: true },
                ]
                  .filter(({ always }) => always || expandedTable)
                  .map(({ col }) => {
                    const sortable = ["name", "date", "type", "owner", "amount", "location"].includes(col);
                    const key = col === "amount corrected" ? "amount" : col;
                    const isActive = sortColumn === key;
                    return (
                      <th
                        key={col}
                        onClick={sortable ? () => this.handleSort(key) : undefined}
                        style={sortable ? { cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" } : {}}
                      >
                        {col.charAt(0).toUpperCase() + col.slice(1)}
                        {sortable && (
                          <span style={{ marginLeft: "0.4rem", opacity: isActive ? 1 : 0.5 }}>
                            {!isActive ? "↕" : sortDirection === "desc" ? "▼" : "▲"}
                          </span>
                        )}
                      </th>
                    );
                  })}
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.map((row, i) => (
                <tr key={i}>
                  <td>{row.name}</td>
                  {expandedTable && <td>{new Date(row.date).toLocaleDateString()}</td>}
                  {expandedTable && <td>{row.type}</td>}
                  {expandedTable && <td>{this.getOwnerUsername(row.owner)}</td>}
                  {expandedTable && <td>{row.tr ? 'Yes' : 'No'}</td>}
                  {expandedTable && <td>{row.amount}</td>}
                  <td>{row.tr ? (row.amount * 0.75).toFixed(2) : row.amount.toFixed(2)}</td>
                  {expandedTable && <td>{row.location}</td>}
                  {expandedTable && <td>{row.note}</td>}
                  {expandedTable && <td>{this.getParticipantsUsernames(row.participants)}</td>}
                  {expandedTable && <td>{this.getOwnerUsername(row.publisher)}</td>}
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn btn-sm btn-info" onClick={() => this.handleShowEditExpenseModal(row)}>📝</button>
                      <button className="btn btn-sm btn-danger" onClick={() => this.handleShowDeleteModal(row)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {editingExpense && (
        <Modal show={showEditExpenseModal} onHide={this.handleCloseEditModal}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Expense</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {message && <p className="alert alert-info">{message}</p>}
            <Form>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={editingExpense.name}
                  onChange={this.handleEditExpenseChange}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Type</Form.Label>
                <Form.Control
                  as="select"
                  name="type"
                  value={editingExpense.type}
                  onChange={this.handleEditExpenseChange}
                >
                  <option value="basic">Basic</option>
                  <option value="groceries">Groceries</option>
                  <option value="transport">Transport</option>
                  <option value="eatout">Eat Out</option>
                  <option value="travel">Travel</option>
                  <option value="other">Other</option>
                </Form.Control>
              </Form.Group>
              <Form.Group>
                <Form.Label>Owner</Form.Label>
                <Form.Control
                  as="select"
                  name="owner"
                  value={editingExpense.owner}
                  onChange={this.handleEditExpenseChange}
                >
                  <option value="">Select Owner</option>
                  {familyMembers.map(member => (
                    <option key={member._id} value={member._id}>{member.username}</option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="TR"
                  name="tr"
                  checked={editingExpense.tr}
                  onChange={this.handleEditExpenseChange}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="number"
                  name="amount"
                  value={editingExpense.amount}
                  onChange={this.handleEditExpenseChange}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Location</Form.Label>
                <Form.Control
                  type="text"
                  name="location"
                  value={editingExpense.location}
                  onChange={this.handleEditExpenseChange}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Participants</Form.Label>
                <div style={{ 
                  border: '1px solid #ced4da', 
                  borderRadius: '0.25rem', 
                  padding: '0.75rem',
                  maxHeight: '150px',
                  overflowY: 'auto'
                }}>
                  {familyMembers
                    .filter(member => !member.username.includes('-common'))
                    .map(member => (
                      <Form.Check
                        key={member._id}
                        type="checkbox"
                        id={`participant-${member._id}`}
                        label={member.username}
                        checked={(editingExpense.participants || []).includes(member._id)}
                        onChange={() => this.handleParticipantsChange(member._id)}
                        style={{ marginBottom: '0.5rem' }}
                      />
                    ))}
                </div>
                <Form.Text className="text-muted">
                  Select all participants who will share this expense
                </Form.Text>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseEditModal}>
              Close
            </Button>
            <Button variant="primary" onClick={this.submitEditExpense}>
              Save Changes
            </Button>
          </Modal.Footer>
        </Modal>
        )}

        {deletingExpense && (
          <Modal show={showDeleteModal} onHide={this.handleCloseDeleteModal}>
            <Modal.Header closeButton>
              <Modal.Title>Confirm Deletion</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {message && <p className="alert alert-danger">{message}</p>}
              Are you sure you want to delete the expense: <strong>{deletingExpense.name}</strong>?
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={this.handleCloseDeleteModal}>
                Cancel
              </Button>
              <Button variant="danger" onClick={this.handleDeleteExpense}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {/* Responsive styles */}
        <style>{`
          .month-navigation-container {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
            align-items: center;
            margin-bottom: 1rem;
            flex-wrap: wrap;
          }
          .month-navigation {
            display: flex;
            align-items: center;
            white-space: nowrap;
            flex-shrink: 0;
          }
          .month-display {
            margin: 0 1rem;
            min-width: fit-content;
          }
          @media (max-width: 600px) {
            .month-navigation-container {
              justify-content: center;
              flex-direction: column;
              gap: 0.75rem;
            }
            .month-navigation {
              order: 1;
            }
            .month-navigation-container button:last-child {
              order: 2;
            }
          }
          .summary-tables-container {
            display: flex;
            justify-content: center;
            align-items: flex-start;
            gap: 2rem;
            flex-wrap: wrap;
            width: 100%;
          }
          .summary-table {
            width: auto;
            min-width: 200px;
            margin: 0 auto;
          }
          .pie-charts-container {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            margin: 2rem 0;
            flex-wrap: wrap;
          }
          .pie-chart-wrapper {
            width: 100%;
            max-width: 400px;
            height: 400px;
          }
          .main-table-container {
            overflow-x: auto;
            width: 100%;
          }
          .main-table {
            width: 100%;
            table-layout: auto;
            border-collapse: collapse;
          }
          .main-table.expanded {
            min-width: 800px;
          }
          @media (min-width: 1200px) {
            .main-table-container {
              display: flex;
              justify-content: center;
            }
            .main-table {
              max-width: 100%;
              margin: 0 auto;
            }
          }
          .main-table th,
          .main-table td {
            border: 1px solid #dee2e6;
            padding: 0.75rem;
            vertical-align: top;
            word-break: break-word;
            max-width: 200px;
          }
          .main-table thead th {
            vertical-align: bottom;
            border-bottom: 2px solid #dee2e6;
            background-color: #f8f9fa;
          }
          .main-table tbody tr:nth-child(even) {
            background-color: rgba(0, 0, 0, 0.05);
          }
          @media (max-width: 900px) {
            .summary-table {
              font-size: 0.9rem;
            }
            .main-table {
              font-size: 0.9rem;
            }
            .main-table th,
            .main-table td {
              padding: 0.5rem;
              max-width: 150px;
            }
          }
          @media (max-width: 768px) {
            .pie-chart-wrapper {
              max-width: 350px;
              height: 350px;
            }
            .summary-table thead {
              display: none;
            }
            .summary-table, .summary-table tbody, .summary-table tr, .summary-table td {
              display: block;
              width: 100%;
            }
            .summary-table tr {
              margin-bottom: 1rem;
            }
            .summary-table td {
              text-align: right;
              padding-left: 50%;
              position: relative;
              border: none;
              border-bottom: 1px solid #eee;
            }
            .summary-table td::before {
              content: attr(data-label);
              position: absolute;
              left: 0;
              width: 50%;
              padding-left: 1rem;
              font-weight: bold;
              text-align: left;
            }
            .summary-tables-container {
              display: block;
            }
            .summary-table {
              margin-bottom: 2rem;
            }
          }
          @media (max-width: 600px) {
            .summary-tables-container {
              gap: 1rem;
            }
            .summary-table {
              font-size: 0.8rem;
              min-width: 150px;
            }
            .main-table {
              font-size: 0.8rem;
            }
            .main-table th,
            .main-table td {
              padding: 0.4rem;
              max-width: 120px;
            }
            .card-body {
              padding: 0.5rem;
            }
            .pie-chart-wrapper {
              max-width: 100%;
              height: 300px;
            }
          }
          @media (max-width: 400px) {
            .summary-tables-container {
              flex-direction: column;
              align-items: center;
            }
            .summary-table {
              width: 100%;
              min-width: 0;
            }
          }
        `}</style>
      </div>
    </div>
    );
  }
}
